import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ThemeToggle from './ThemeToggle';
import './Navbar.css';

/* The bar used to carry nine links at one level, which wrapped into a vertical
   stack below 768px. Three destinations stay in the bar; the rest are secondary
   views that live under Narzędzia. */
const PRIMARY_LINKS = [
  { to: '/', label: 'Produkty', end: true },
  { to: '/offers', label: 'Moje Oferty', end: false },
  { to: '/templates', label: 'Szablony', end: false },
];

const TOOL_LINKS = [
  { to: '/product-editor', label: 'Dodaj Produkt' },
  { to: '/products-table', label: 'Tabela' },
  { to: '/archive', label: 'Archiwum' },
  { to: '/price-editor', label: 'Edytor Cen' },
  { to: '/gallery', label: 'Galeria Zdjęć' },
  { to: '/category-manager', label: 'Kategorie' },
];

const Navbar: React.FC = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [toolsOpen, setToolsOpen] = useState(false);
  const toolsRef = useRef<HTMLDivElement>(null);
  const toolsButtonRef = useRef<HTMLButtonElement>(null);

  const closeTools = useCallback(() => setToolsOpen(false), []);

  // Close the menu when the route changes, so it does not stay open over the
  // page it just navigated to.
  useEffect(closeTools, [location.pathname, closeTools]);

  useEffect(() => {
    if (!toolsOpen) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!toolsRef.current?.contains(event.target as Node)) closeTools();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeTools();
        toolsButtonRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [toolsOpen, closeTools]);

  const toolsActive = TOOL_LINKS.some((link) => location.pathname.startsWith(link.to));

  return (
    <nav className="app-nav">
      <div className="app-nav__inner">
        <Link to="/" className="app-nav__brand">
          <span className="app-nav__mark" aria-hidden="true">
            W
          </span>
          <span className="app-nav__brand-text">
            <strong>Whisky and Wine Place</strong>
          </span>
        </Link>

        <div className="app-nav__links">
          {PRIMARY_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.end}
              className={({ isActive }) =>
                isActive ? 'app-nav__link app-nav__link--active' : 'app-nav__link'
              }
            >
              {link.label}
            </NavLink>
          ))}

          <div className="app-nav__menu" ref={toolsRef}>
            <button
              ref={toolsButtonRef}
              type="button"
              className={
                toolsActive || toolsOpen
                  ? 'app-nav__link app-nav__link--active app-nav__trigger'
                  : 'app-nav__link app-nav__trigger'
              }
              aria-expanded={toolsOpen}
              aria-haspopup="true"
              onClick={() => setToolsOpen((open) => !open)}
            >
              Narzędzia
              <span className="app-nav__caret" aria-hidden="true" />
            </button>

            {toolsOpen && (
              <div className="app-nav__dropdown" role="menu">
                {TOOL_LINKS.map((link) => (
                  <NavLink
                    key={link.to}
                    to={link.to}
                    role="menuitem"
                    className={({ isActive }) =>
                      isActive
                        ? 'app-nav__dropdown-item app-nav__dropdown-item--active'
                        : 'app-nav__dropdown-item'
                    }
                  >
                    {link.label}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="app-nav__actions">
          <Link to="/offer-generator" className="btn btn-primary btn-sm">
            Generuj Ofertę
          </Link>
          <ThemeToggle />
          <span className="app-nav__user" title={user?.username}>
            <span className="app-nav__avatar" aria-hidden="true">
              {user?.username?.charAt(0).toUpperCase() ?? '?'}
            </span>
            <span className="app-nav__username">{user?.username}</span>
          </span>
          <button onClick={logout} className="btn btn-secondary btn-sm" type="button">
            Wyloguj
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
